<template>
	<v-card flat class="position-relative d-flex flex-column style-fixed-card">
		<!-- Loading Overlay -->
		<div v-if="loadingState" class="card-loadingState-overlay">
			<v-progress-linear indeterminate color="primary-green" size="48" width="4" />
		</div>

		<!-- Scrollable Content Body -->
		<v-card-text class="main-container overflow-y-auto pa-0">
			<v-divider></v-divider>

			<!-- Top Header Action Bar -->
			<v-row no-gutters class="border-bottom bg-white flex-nowrap">
				<v-col cols="6" class="pa-3 px-5 d-flex justify-space-between align-center border-right">
					<span class="text-body-large font-weight-bold text-grey text--darken-3 ml-2">{{ $t('planning_driver') }}</span>
					<div class="d-flex align-center">
						<span class="text-body-2 text-grey mr-4">{{ $t('planning_driver_fixed_on_duty') }}</span>
						<v-switch
							v-model="changedDuty.fixed"
							:disabled="!edit || duty?.fixedBy === 'absence'"
							hide-details
							color="primary-green"
							density="compact"
						></v-switch>
					</div>
				</v-col>
				<v-divider vertical></v-divider>
				<v-col cols="6" class="pa-3 pr-7 d-flex justify-space-between align-center">
					<span class="text-body-large font-weight-bold text-grey text--darken-3 ml-2">{{ $t('duty_duty') }}</span>
					<div class="d-flex align-center">
						<v-btn
							v-if="!dutyIsUnallocated && duty?.type === 'duty'"
							variant="flat"
							color="grey-lighten-4"
							class="text-black text-uppercase mr-4 font-weight-bold custom-disabled-color"
							size="small"
							:disabled="hasDriverChanged || !edit || changedDuty.type === 'absence'"
							@click="triggerUnassign"
						>
							<v-icon start icon="mdi-account-off" size="14"></v-icon>
							{{ $t('duty_unassign') }}
						</v-btn>
						<v-btn
							variant="flat"
							size="31"
							color="grey-lighten-4"
							class="rounded-lg custom-disabled-color"
							@click="triggerDelete"
							:disabled="hasDriverChanged || !edit || changedDuty.type === 'absence'"
						>
							<v-icon color="grey" icon="mdi-delete"></v-icon>
						</v-btn>
					</div>
				</v-col>
			</v-row>

			<v-divider></v-divider>

			<!-- Main Input Controls -->
			<v-row no-gutters class="pt-2 flex-nowrap">
				<!-- Left Column -->
				<v-col cols="6" class="pr-4 pt-1 pl-4 border-right">
					<v-card variant="flat" class="pa-3 mb-4 bg-grey-lighten-4 custom-border">
						<label class="text-caption text-grey d-block">{{ $t('planning_driver') }}</label>
						<DropDownWithChips
							v-model="changedDuty.user"
							:items="allDriversData || {}"
							label-key="displayName"
							search-placeholder="driver"
							:disabled="hasDutyOrTypeChanged || !edit"
						>
						</DropDownWithChips>
					</v-card>

					<!-- Only renders the card if at least ONE chip condition is met -->
					<v-card v-if="hasAnyChip" variant="flat" class="content-card">
						<div class="d-flex flex-nowrap align-center gap-2 overflow-x-auto">
							<v-chip v-if="duty?.fixed" size="x-small" color="grey-lighten-1">
								<v-icon start icon="mdi-lock" size="14"></v-icon>
								{{ fixedByText }}
							</v-chip>
							<v-chip v-if="isNightShift" size="x-small" color="indigo-lighten-5">
								<v-icon start icon="mdi-clock" size="14"></v-icon>
								{{ shiftString }}
							</v-chip>
							<v-chip v-if="isMidShift" size="x-small" color="orange-darken-1">
								<v-icon start icon="mdi-clock" size="14"></v-icon>
								{{ shiftString }}
							</v-chip>
							<v-chip v-if="isEarlyShift" size="x-small" color="green-lighten-1">
								<v-icon start icon="mdi-clock" size="14"></v-icon>
								{{ shiftString }}
							</v-chip>
							<v-chip v-if="isLateShift" size="x-small" color="green-lighten-1">
								<v-icon start icon="mdi-clock" size="14"></v-icon>
								{{ shiftString }}
							</v-chip>
							<v-chip v-if="isCompensationDay" size="x-small">
								<v-icon start icon="mdi-arrow-collapse-horizontal" size="14"></v-icon>
								{{ $t('duty_dayType_compensation') }}
							</v-chip>
							<v-chip v-if="isRestDay" size="x-small">
								<v-icon start icon="mdi-sleep" size="14"></v-icon>
								{{ $t('duty_dayType_rest') }}
							</v-chip>
							<v-chip v-if="isVacation" size="x-small">
								<v-icon start icon="mdi-bag-checked" size="14"></v-icon>
								{{ $t('duty_dayType_vacation') }}
							</v-chip>
							<v-chip v-if="isWorkDay || isReserveDay" size="x-small">
								<v-icon start icon="mdi-clock-outline" size="14"></v-icon>
								{{ $t('duty_dayType_duty') }}
							</v-chip>
						</div>
					</v-card>

					<v-card v-if="hasAnyMetOrUnmet" variant="flat" class="content-card">
						<div v-if="duty?.hasMet || duty?.hasUnmet" class="mb-3">
							<v-chip v-if="duty?.hasMet" size="x-small" color="green-lighten-5" class="text-success font-weight-bold mr-2" label>
								<v-icon start icon="mdi-emoticon-happy-outline" size="14"></v-icon>
								{{ $t('duty_met') }}
							</v-chip>
							<v-chip v-if="duty?.hasUnmet" size="x-small" color="orange-lighten-5" class="text-warning font-weight-bold" label>
								<v-icon start icon="mdi-emoticon-frown-outline" size="14"></v-icon>
								{{ $t('duty_unmet') }}
							</v-chip>
						</div>
						<v-list v-if="metAndUnmetItems.length" density="compact" class="pa-0 bg-transparent">
							<v-list-item v-for="(item, i) in metAndUnmetItems" :key="i" class="px-0 min-h-0">
								<div class="d-flex">
									<v-icon icon="mdi-information-outline" size="x-small" color="grey" class="mr-2"></v-icon>
									<div class="text-body-2 text-wrap">
										{{ $t(item.messageKey, getMessageParams(item.messageParams)) }}
									</div>
								</div>
							</v-list-item>
						</v-list>
					</v-card>

					<v-card v-if="openExchange && !dutyIsUnallocated" variant="flat" class="pa-3 border bg-white rounded-lg mb-4">
						<v-chip size="x-small" color="indigo-lighten-5" class="text-darken-2 mb-2">
							<v-icon start icon="mdi-swap-vertical" size="14" />
							{{ $t('duty_in_exchange') }}
						</v-chip>
						<div class="text-body-2 exchange-preference-text">
							<span class="font-weight-bold mr-1">{{ $t('exchange_preference') }}</span>
							<span v-for="(preference, index) in openExchange.preferences" :key="preference">
								{{ index ? ', ' : '' }}{{ $t(preference + 'Duty') }}
							</span>
						</div>
					</v-card>
				</v-col>

				<v-divider vertical></v-divider>

				<!-- Right Column -->
				<v-col cols="6" class="pl-4 pt-1 pr-4 pb-3">
					<v-card variant="flat" class="pa-3 mb-4 bg-grey-lighten-4 custom-border">
						<div class="mb-4">
							<label class="text-caption text-grey d-block">{{ $t('planning_type') }}</label>
							<v-select
								v-model="changedDuty.type"
								:items="typeList"
								item-title="name"
								item-value="id"
								variant="underlined"
								density="compact"
								hide-details
								class="pt-0"
								:disabled="hasDriverChanged || !edit"
								@update:modelValue="onTypeChange"
							></v-select>
						</div>
						<div>
							<label class="text-caption text-grey d-block">{{ $t('planning_disposition') }}</label>
							<div class="d-flex align-center">
								<DropDownWithChips
									v-if="allDispositions"
									v-model="changedDuty.duty"
									:items="allDispositions || {}"
									label-key="name"
									search-placeholder="duty"
									:disabled="!edit || hasDriverChanged || changedDuty.type !== 'duty'"
								>
								</DropDownWithChips>
								<v-icon icon="mdi-plus" :disabled="!edit" color="grey" class="cursor-pointer" @click="openplanningCreateDutydialogNew">
								</v-icon>
							</div>
							<span v-if="isDispositionInvalid" class="text-caption text-error d-block mt-1">
								{{ $t('report.form.required') }}
							</span>
						</div>
					</v-card>

					<v-card v-if="duty?.hasViolations || showAddToExchange" variant="flat" class="pa-3 border bg-white rounded-lg min-h-100">
						<v-chip v-if="duty?.hasViolations" size="x-small" color="red-lighten-5" class="text-error mb-2">
							<v-icon start icon="mdi-alert-circle-outline" size="14" />
							{{ $t('duty_violations') }}
						</v-chip>
						<v-list v-if="duty?.user && duty.violations?.length > 0" density="compact" class="pa-0">
							<v-list-item v-for="vio in duty.violations" :key="vio._id" class="px-0 border-bottom-light">
								<v-list-item-title class="text-body-2 text-wrap">
									{{ $t(vio.messageKey, getMessageParams(vio.messageParams)) }}
									<div v-if="vio.rule">{{ vio.rule }}</div>
									<div v-if="vio.category">{{ $t('disposition.text_category') }} : {{ $t(vio.category) }}</div>
								</v-list-item-title>
								<template #append>
									<v-tooltip v-if="vio.weight === 0" :text="getAgreementReasonByViolation(vio)">
										<template #activator="{ props }">
											<v-icon v-bind="props" color="success" size="small"> mdi-check-circle </v-icon>
										</template>
									</v-tooltip>
									<v-tooltip
										v-else
										:text="$t(isPreviewDuty ? 'tooltips.violation_confirm_disabled_preview' : 'confirm')"
										:disabled="!isPreviewDuty"
										open-delay="500"
										max-width="150"
									>
										<template #activator="{ props }">
											<span v-bind="props">
												<v-btn
													size="x-small"
													variant="outlined"
													color="primary"
													class="ml-2"
													:disabled="isPreviewDuty"
													@click="openAgreementReason(vio)"
												>
													{{ $t('confirm') }}
												</v-btn>
											</span>
										</template>
									</v-tooltip>
								</template>
							</v-list-item>
						</v-list>
						<v-tooltip v-if="isPreviewDuty" :text="$t('tooltips.duty_add_to_exchange_disabled_preview')" open-delay="500" max-width="150">
							<template #activator="{ props }">
								<span v-bind="props">
									<v-btn
										v-if="showAddToExchange"
										block
										variant="outlined"
										color="primary"
										size="small"
										class="mt-4"
										:disabled="true"
										@click="handleAddToExchange"
									>
										{{ $t('duty_addToExchange') }}
									</v-btn>
								</span>
							</template>
						</v-tooltip>

						<v-btn v-else-if="showAddToExchange" block variant="outlined" color="primary" size="small" class="mt-4" @click="handleAddToExchange">
							{{ $t('duty_addToExchange') }}
						</v-btn>
					</v-card>
				</v-col>
			</v-row>

			<v-divider></v-divider>

			<v-divider></v-divider>
		</v-card-text>

		<v-divider class="flex-shrink-0"></v-divider>

		<!-- Notes Section -->
		<div class="sticky-notes-section">
			<div class="pa-4 bg-grey-lighten-4">
				<h3 class="text-body-large font-weight-bold">{{ $t('planning_disposition_notes') }}</h3>
				<v-textarea
					v-model="changedDuty.internalNotes"
					variant="plain"
					:placeholder="$t('disposition.notes.placeholder')"
					rows="1"
					hide-details
					:disabled="!edit"
					class="italic-placeholder"
				></v-textarea>
			</div>

			<!-- Fixed Bottom Section: Pending Changes Banner -->

			<div class="save-changes-div border-top flex-shrink-0" v-if="hasChanges && changeHistory.length > 0">
				<div class="d-flex flex-column">
					<div class="text-subtitle-2 mb-2 font-weight-bold d-flex align-center">
						<v-icon color="warning" class="mr-1" size="small">mdi-information</v-icon>
						<span>{{ t('button.pending_changes') }}</span>
					</div>
					<v-divider class="mb-2" />
					<div v-for="change in changeHistory" :key="change.key" class="text-caption mb-1">
						<span class="font-weight-bold text-capitalize">{{ change.property }}:</span>
						<span class="text-grey-darken-1 mx-1">{{ change.from }}</span>
						<v-icon icon="mdi-arrow-right" size="x-small" />
						<span class="text-warning font-weight-bold ms-1">{{ change.to ? change.to : DEFAULT_NONE }}</span>
					</div>

					<!-- Hide the Apply button when preview mode is active -->
					<div v-if="!isActive" class="d-flex justify-end mt-2">
						<v-btn
							color="grey-lighten-1"
							variant="flat"
							class="px-6 font-weight-bold"
							size="small"
							:disabled="isDispositionInvalid"
							@click="triggerUpdate(false)"
						>
							{{ $t('action_apply') }}
						</v-btn>
					</div>
				</div>
			</div>
		</div>
		<div v-if="isAffectedByPreview" class="affected-duty-bar border-top flex-shrink-0 d-flex align-center">
			<v-icon icon="mdi-alert" color="warning" size="small" class="mr-3" />
			<div class="d-flex flex-column">
				<span class="text-body-2">
					{{ $t('duty_affected_by_preview', { names: editorsText }) }}
				</span>
			</div>
		</div>
		<!-- Fixed Bottom Section: Action Buttons -->
		<v-card-actions class="pa-3 bg-white flex-shrink-0">
			<v-btn v-if="hasChanges && (edit || partialEdit)" variant="text" color="#000000DE" class="px-6 font-weight-bold" @click="close">
				{{ $t('action_cancel') }}
			</v-btn>
			<v-spacer></v-spacer>
			<v-btn
				v-if="(edit || partialEdit) && hasChanges && (!isPastOrPublishedDay || !isActive)"
				variant="flat"
				color="#F5F5F5"
				class="px-6 font-weight-bold"
				:disabled="isDispositionInvalid"
				@click="triggerUpdate"
			>
				{{ $t('action_save') }}
			</v-btn>
			<v-btn
				v-if="isPastOrPublishedDay && hasChanges"
				variant="flat"
				color="teal-darken-4"
				:disabled="isDispositionInvalid"
				:loading="previewButtonLoading"
				@click="() => handlePreviewButtonClick(() => triggerUpdate())"
			>
				<v-progress-circular v-if="previewButtonLoading" indeterminate size="18" width="2" />
				<span v-else>{{ $t(previewButtonLabelKey) }}</span>
			</v-btn>
			<v-btn
				v-if="!hasChanges || (!edit && !partialEdit)"
				variant="text"
				:color="!hasChanges ? '#000000DE' : 'primary-green darken-1'"
				:class="!hasChanges ? 'px-6 font-weight-bold' : ''"
				@click="close"
			>
				{{ $t(!edit && !partialEdit ? 'action_ok' : 'action_close') }}
			</v-btn>
		</v-card-actions>

		<!-- Dialogs -->
		<v-dialog v-model="showAgreementReason" max-width="50%" :persistent="false" @click:outside="closeAgreementReason">
			<v-form ref="resolveViolation" @submit.prevent="confirmViolation">
				<v-card>
					<v-card-title class="text-h5 bg-grey-lighten-2">
						{{ $t('solution.solveViolation_reason_title') }}
					</v-card-title>
					<v-divider />
					<v-card-text>
						<v-text-field v-model="agreementReason" :label="$t('solution.extraWorkRequest_reason')" :rules="reasonRules" />
					</v-card-text>
					<v-divider />
					<v-card-actions>
						<v-spacer />
						<v-btn color="primary" variant="text" @click="closeAgreementReason">
							{{ $t('action_cancel') }}
						</v-btn>
						<v-btn color="success" variant="elevated" @click="confirmViolation">
							{{ $t('confirm') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>
	</v-card>

	<AddCommentDialog
		v-if="showCommentDialog"
		:title="commentDialogTitle"
		:model-value="showCommentDialog"
		:action-type="currentActionMode?.toLowerCase()"
		:show-preview="currentActionMode === 'DELETE' || currentActionMode === 'UNASSIGN' ? isPastOrPublishedDay : false"
		:is-preview-active="currentActionMode === 'DELETE' || currentActionMode === 'UNASSIGN' ? isActive : false"
		@update="closeCommentDialog"
		@submit="handleSubmit"
	/>
	<PlanningCreateDutyDialog v-if="createDutyStore.isOpen" @save="emit('close', false)" />
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeMount } from 'vue';
import type { VForm } from 'vuetify/components';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useDutyStore } from '@/rennovation/planning-view/store';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';
import { useUserStore } from '@/rennovation/planning-view/store/useUserStore';
import { useUIStore } from '@/rennovation/planning-view/store';
import { usePlanningDayStatus } from '@/rennovation/planning-view/composables/useDayStatus';
import { ApiDuty, DutyViolation } from '@/rennovation/planning-view/types/api/duties-response';
import { AgreementPayload, DutyChangeLogPayload } from '@/rennovation/planning-view/types/payloads/duty-payloads';
import { DutyBreak } from '@/rennovation/planning-view/types/api/duties-response';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import DropDownWithChips from '../../Controls/DropDownWithChips.vue';
import AddCommentDialog from './AddCommentDialog.vue';
import isEqual from 'lodash/isEqual';
import { useDutyFormValidator } from '@/rennovation/planning-view/composables/useDutyFormValidator';
import useDateTime from '../../../../../../components/composables/useDateTime.js';
import { usePlanningCreateDutyStore } from '@/rennovation/planning-view/store/usePlanningCreateDutyStore';
import PlanningCreateDutyDialog from '@/rennovation/planning-view/components/Layout/Modals/PlanningCreateDutyDialog.vue';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';

const props = defineProps<{ dutyID: string; customDuty?: ApiDuty | null }>();
const emit = defineEmits(['update:modelValue', 'showSnackbar', 'close', 'handleTabChange', 'duty-updated']);
const { t, te } = useI18n();
const dutyStore = useDutyStore();
const planningStore = usePlanningStore();
const dayStore = useDayStore();
const userStore = useUserStore();
const uiStore = useUIStore();
const createDutyStore = usePlanningCreateDutyStore();
const alertStore = useAlertStore();
const DEFAULT_NONE = t('presets.none');

type ChangedDutyState = {
	duty: string | null;
	type: string | null;
	fixed: boolean;
	user: string | null;
	internalNotes: string;
	exchange?: boolean;
};

type ChangeHistoryItem = {
	key: string;
	property: string;
	from: unknown;
	to: unknown;
};
const { formatDateTime } = useDateTime();

const duty = ref<ApiDuty | null>(null);
const loadingState = ref<boolean>(true);
const edit = ref(false);
const partialEdit = ref(false);
const resolveViolation = ref<any>(null);
const selectedViolation = ref<DutyViolation | null>(null);
const agreementReason = ref<string>('');
const showAgreementReason = ref<boolean>(false);
const showCommentDialog = ref<boolean>(false);
const changeComment = ref<string>('');
const currentActionMode = ref<'UPDATE' | 'DELETE' | 'UNASSIGN' | null>(null);
const outerSaveButtonClicked = ref(false);
const reasonRules = [(v: string) => !!v?.trim() || t('report.form.required')];
const { isPastOrPublishedDay, assignDutyPropertyFixed } = usePlanningDayStatus(duty, dayStore.daysRef);
const { evaluateEditPermissions } = useDutyFormValidator();
const { isActive, previewButtonLoading, previewButtonLabelKey, handlePreviewButtonClick, getEditorNames } = useDutyChangePreview();

defineOptions({
	inheritAttrs: false,
});

const changedDuty = ref<ChangedDutyState>({
	duty: null,
	type: null,
	fixed: false,
	user: null,
	internalNotes: '',
	exchange: false,
});

const rawDuty = ref<ChangedDutyState>({
	duty: null,
	type: null,
	fixed: false,
	user: null,
	internalNotes: '',
	exchange: false,
});

const isPreviewDuty = computed(() => {
	const preview = duty.value?.preview;
	return preview !== null && preview !== undefined;
});
const openExchange = computed(() => duty.value?.exchanges?.find((exchange) => exchange.status === 'open') ?? null);
const isReserveDay = computed(() => ['reserve'].includes(duty.value?.type as string));
const isCompensationDay = computed(() => ['compensation'].includes(duty.value?.type as string));
const isAbsenceDay = computed(() => ['absence'].includes(duty.value?.type as string));
const isRestDay = computed(() => duty.value?.type === 'rest');
const isVacation = computed(() => duty.value?.type === 'vacation');
const hasNoUser = computed(() => duty.value?.user === null || duty.value?.user === undefined); // for unassigned duties, user can be null or undefined

const isWorkDay = computed(() => changedDuty.value.type === 'duty');
const isDispositionInvalid = computed(() => {
	return isWorkDay.value && !changedDuty.value.duty;
});
const hasAnyChip = computed(() => {
	return !!(
		duty.value?.fixed ||
		isNightShift.value ||
		isMidShift.value ||
		isEarlyShift.value ||
		isLateShift.value ||
		isCompensationDay.value ||
		isRestDay.value ||
		isVacation.value ||
		isWorkDay.value ||
		isReserveDay.value
	);
});
const metAndUnmetItems = computed(() => [...(duty.value?.met || []), ...(duty.value?.unmet || [])]);

const hasAnyMetOrUnmet = computed(() => {
	return !!(duty.value?.hasMet || duty.value?.hasUnmet || metAndUnmetItems.value.length > 0);
});
const fixedByText = computed(() => {
	if (['absence', 'rotation'].includes(duty.value?.fixedBy)) {
		return t(`duty_fixed_${duty.value.fixedBy}`);
	}
	return t('duty_fixed_null');
});

const showAddToExchange = computed(() => {
	const isDutyType = duty.value?.type === 'duty';
	const dayKey = duty.value?.day;
	const idKey = duty.value?.id;
	const hasExchange = !!(dayKey && idKey && dutyStore.unallocatedDutiesMappedByDay[dayKey]?.[idKey]?.exchange);
	const isNotExchanging = !hasExchange;
	const hasDayRef = !!(dayKey && dayStore.daysRef[dayKey]);
	const dayIndex = dayKey ? dayStore.daysRef[dayKey] : undefined;
	const isPublished = dayIndex !== undefined && dayStore.days[dayIndex]?.data?.status === 'published';
	return hasNoUser.value && isDutyType && isNotExchanging && hasDayRef && isPublished;
});

const isAffectedByPreview = computed(() => {
	const id = props.dutyID;
	return !!(
		duty.value?.affectedByAnyPreview ||
		dutyStore.dutiesMappedById[id]?.affectedByAnyPreview ||
		dutyStore.unallocatedDutiesById[id]?.affectedByAnyPreview
	);
});

const editors = computed(() => getEditorNames(duty.value?.affectedByPreview));
const editorsText = computed(() => editors.value.join(', '));

const typeList = computed(() => {
	const currentType = duty.value?.type;
	const isAllocated = !hasNoUser.value;
	let items = [];

	if (isAllocated) {
		items = [
			{ id: 'duty', name: t('duty_dayType_duty') },
			{ id: 'reserve', name: t('duty_dayType_reserve') },
			{ id: 'compensation', name: t('duty_dayType_compensation') },
			{ id: 'rest', name: t('duty_dayType_rest') },
		];
	} else {
		items = [
			{ id: 'duty', name: t('duty_dayType_duty') },
			{ id: 'reserve', name: t('duty_dayType_reserve') },
		];
	}

	if (currentType && !items.some((item) => item.id === currentType)) {
		items.push({ id: currentType, name: t(`duty_dayType_${currentType}`) });
	}

	return items;
});

const openplanningCreateDutydialogNew = () => {
	const selectedDriver = driversData.value.find((d) => d._id === changedDuty.value.user);
	createDutyStore.openWithContext({
		driverName: selectedDriver?.displayName,
		driverId: selectedDriver?._id ?? '',
		date: duty.value?.dayObject?.dateString,
	});
};

const isNightShift = computed(() => duty.value?.shift === 'night');
const isMidShift = computed(() => duty.value?.shift === 'mid');
const isEarlyShift = computed(() => duty.value?.shift === 'early');
const isLateShift = computed(() => duty.value?.shift === 'late');
const dutyIsUnallocated = computed(() => duty.value?.user === null || duty.value?.user === undefined);
const allDriversData = computed(() => {
	return userStore.currentDayDrivers?.data;
});
const driversData = computed(() => {
	const data = allDriversData.value;
	return [...(data?.unallocated ?? []), ...(data?.reserve ?? []), ...(data?.all ?? [])];
});
const allDispositions = computed(() => dutyStore.currentDayDuties?.data);
const hasChanges = computed(() => {
	return !isEqual(rawDuty.value, changedDuty.value);
});
const shiftString = computed(() => (duty.value?.shift ? t('duty_shift_' + duty.value?.shift) : ''));

watch(
	() => hasChanges.value,
	(newVal: boolean) => {
		uiStore.setHasPendingChanges(newVal);
	},
	{ immediate: true }
);

const hasDriverChanged = computed(() => changedDuty.value.user !== rawDuty.value?.user);
const hasDutyOrTypeChanged = computed(() => changedDuty.value.duty !== rawDuty.value?.duty || changedDuty.value.type !== rawDuty.value?.type);

const TYPE_LABEL_KEYS: Record<string, string> = {
	duty: 'duty_dayType_duty',
	reserve: 'duty_dayType_reserve',
	compensation: 'duty_dayType_compensation',
	rest: 'duty_dayType_rest',
};

const propertyLabels = computed<Record<(typeof CHANGE_KEYS)[number], string>>(() => ({
	user: t('planning_driver'),
	duty: t('duty_duty'),
	type: t('planning_type'),
	fixed: t('planning_driver_fixed_on_duty'),
	internalNotes: t('planning_internal_notes'),
}));

const CHANGE_KEYS = ['type', 'fixed', 'internalNotes', 'duty', 'user'] as const;

function resolveDutyLabel(dutyId: unknown): string | undefined {
	if (dutyId == null) return undefined;
	const duty = dutyStore.dutiesMappedById[dutyId] || dutyStore.unallocatedDutiesById[dutyId];
	if (!duty) return undefined;
	return duty.nameKey != null ? t(duty.nameKey) : duty.name;
}

const changeHistory = computed<ChangeHistoryItem[]>(() => {
	if (!rawDuty.value || !changedDuty.value) return [];
	const changes: ChangeHistoryItem[] = [];

	for (const key of CHANGE_KEYS) {
		const rawOld = rawDuty.value[key];
		const rawNew = changedDuty.value[key];

		if (rawNew === rawOld) continue;

		let oldValue: unknown = rawOld;
		let newValue: unknown = rawNew;

		switch (key) {
			case 'user': {
				const oldDriver = driversData.value.find((user) => user._id === rawOld);
				const newDriver = driversData.value.find((user) => user._id === rawNew);
				oldValue = oldDriver?.displayName || DEFAULT_NONE;
				newValue = newDriver?.displayName || DEFAULT_NONE;
				break;
			}
			case 'duty': {
				oldValue = rawOld != null ? (resolveDutyLabel(rawOld) ?? DEFAULT_NONE) : DEFAULT_NONE;
				newValue = resolveDutyLabel(rawNew);
				break;
			}
			case 'type': {
				const oldKey = typeof rawOld === 'string' || typeof rawOld === 'number' ? TYPE_LABEL_KEYS[rawOld] : undefined;
				const newKey = typeof rawNew === 'string' || typeof rawNew === 'number' ? TYPE_LABEL_KEYS[rawNew] : undefined;

				oldValue = oldKey ? t(oldKey) : rawOld;
				newValue = newKey ? t(newKey) : rawNew;
				break;
			}
			case 'fixed': {
				oldValue = rawOld ? t('true') : t('false');
				newValue = rawNew ? t('true') : t('false');
				break;
			}
		}

		changes.push({
			key,
			property: propertyLabels.value[key] || key,
			from: oldValue,
			to: newValue,
		});
	}
	return changes;
});

const CRITICAL_COMMENT_FIELDS = ['user', 'day', 'fixed', 'type'];
const hasCriticalFieldChanges = computed(() => {
	return changeHistory.value.some((change) => CRITICAL_COMMENT_FIELDS.includes(change.key));
});
const showCommentDialogFields = computed(() => {
	return changeHistory.value.some((change) => ['user', 'duty', 'type'].includes(change.key));
});
const isDefaultForFixed = computed(() => {
	return duty.value?.day ? !assignDutyPropertyFixed(dayStore.daysRef[duty.value.day]) : false;
});
const commentDialogTitle = computed(() => t('add_comment_title', { module: t('duty_lowercase') }));

const onTypeChange = (newType: string): void => {
	if (newType === 'duty') {
		changedDuty.value.duty = rawDuty.value.duty;
	} else {
		changedDuty.value.duty = null;
	}
};

const getMessageParams = (messageParams) => {
	const params = {};
	if (messageParams && Object.keys(messageParams).length) {
		for (const key of Object.keys(messageParams)) {
			const rawValue = messageParams[key];
			if (te(rawValue)) {
				params[key] = t(rawValue);
			} else {
				const date = new Date(rawValue);
				if (typeof rawValue === 'string' && /\d{4}-\d{2}-\d{2}/.test(rawValue) && !isNaN(date.getTime())) {
					params[key] = formatDateTime(date.toISOString());
				} else {
					params[key] = typeof rawValue === 'string' ? t(rawValue) : rawValue;
				}
			}
		}
	}
	return params;
};

const getAgreementReasonByViolation = (violation: DutyViolation): string | undefined => {
	const match = duty.value?.violations?.find((vio) => vio.weight === 0 && vio?.agreement?.id === violation?.agreement?.id);
	return match?.agreement?.comment;
};

const openAgreementReason = (violation: DutyViolation): void => {
	selectedViolation.value = violation;
	showAgreementReason.value = true;
};

const closeAgreementReason = (): void => {
	agreementReason.value = '';
	resolveViolation.value?.reset();
	showAgreementReason.value = false;
};

const confirmViolation = async (): Promise<void> => {
	try {
		const { valid } = await resolveViolation.value?.validate();
		if (!valid) return;

		const agreement: AgreementPayload = {
			key: 'solution.violation.acknowledge',
			duty: duty.value!.id,
			user: duty.value!.user,
			comment: agreementReason.value,
			params: {
				params: {
					weight: selectedViolation.value!.weight,
					messageKey: selectedViolation.value!.messageKey,
					messageParams: selectedViolation.value!.messageParams,
				},
			},
		};

		const response: DutyChangeLogPayload = await planningStore.createAgreement(agreement);

		if (response?.status === 201 && selectedViolation.value && duty.value) {
			const confirmedViolation = selectedViolation.value;

			duty.value = {
				...duty.value,
				violations: duty.value.violations?.map((v) => (isEqual(v, confirmedViolation) ? { ...v, weight: 0, agreement: response.data } : v)),
			};
		}

		closeAgreementReason();
	} catch (error) {
		const errorMessage = error?.response?.data?.message || t('data_load_failed');
		alertStore.showAlert(errorMessage, 'error');
		throw error;
	}
};

const handleAddToExchange = async (): Promise<void> => {
	const dutyId = duty.value?.id;
	if (!dutyId) return;

	try {
		await dutyStore.addToExchange(dutyId);
		const updatedDuty = await new Promise((resolve) => setTimeout(() => resolve(init(dutyId)), 1000));
		emit('duty-updated', updatedDuty);
	} catch (error) {
		const errorMessage = error?.response?.data?.message || t('data_load_failed');
		alertStore.showAlert(errorMessage, 'error');
		throw error;
	}
};

const triggerUnassign = (): void => {
	currentActionMode.value = 'UNASSIGN';
	if (isPastOrPublishedDay.value) {
		showCommentDialog.value = true;
		return;
	}
	handleSubmit();
};

const triggerDelete = (): void => {
	currentActionMode.value = 'DELETE';
	if (isPastOrPublishedDay.value) {
		showCommentDialog.value = true;
		return;
	}
	handleSubmit();
};

const triggerUpdate = (isOuterSave = true): void => {
	outerSaveButtonClicked.value = isOuterSave;
	currentActionMode.value = 'UPDATE';
	if (isPastOrPublishedDay.value && showCommentDialogFields.value) {
		showCommentDialog.value = true;
		return;
	}
	handleSubmit();
};

const handleSubmit = async (comment = ''): Promise<void> => {
	changeComment.value = comment;
	showCommentDialog.value = false;

	try {
		switch (currentActionMode.value) {
			case 'UNASSIGN':
				await handleUnassignDuty();
				break;
			case 'DELETE':
				await handleDeleteDuty();
				break;
			case 'UPDATE':
				await updateDuty();
				break;
		}
	} catch (error) {
		console.error('Failed executing handle submit action:', error);
	} finally {
		currentActionMode.value = null;
	}
};

const handleUnassignDuty = async (): Promise<void> => {
	try {
		await dutyStore.unassignDuty({
			id: duty.value!.id,
			changeComment: changeComment.value,
		});
		emit('close', false);
	} catch (error) {
		console.error('Failed to unassign duty:', error);
	}
};

const handleDeleteDuty = async (): Promise<void> => {
	try {
		await dutyStore.deleteDuty({
			dutyId: duty.value!.id,
			changeComment: changeComment.value,
		});
		emit('close', false);
	} catch (error) {
		const errorMessage = error?.response?.data?.message || t('data_load_failed');
		alertStore.showAlert(errorMessage, 'error');
		throw error;
	}
};

const DUTY_TYPE_MAP: Record<string, { name: string; nameKey: string }> = {
	rest: { name: 'R', nameKey: 'duty_dayType_rest_short' },
	compensation: { name: 'A', nameKey: 'duty_dayType_compensation_short' },
	vacation: { name: 'AF', nameKey: 'duty_dayType_vacation_short' },
	absence: { name: 'Abs', nameKey: 'duty_dayType_absence_short' },
	reserve: { name: 'SD', nameKey: 'duty_dayType_reserve_short' },
};

watch(
	[() => changedDuty.value.user, () => changedDuty.value.duty, () => changedDuty.value.type],
	([newUser, newDuty, newType], [oldUser, oldDuty, oldType]) => {
		if (loadingState.value) return;
		const hasChanged = newUser !== oldUser || newDuty !== oldDuty || newType !== oldType;
		const hasChangedState =
			changedDuty.value.user !== rawDuty.value.user || changedDuty.value.duty !== rawDuty.value.duty || changedDuty.value.type !== rawDuty.value.type;

		if (hasChangedState) {
			changedDuty.value.internalNotes = '';
		} else {
			changedDuty.value.internalNotes = rawDuty.value.internalNotes;
		}

		if (hasChanged && (newUser || newDuty || newType) && isDefaultForFixed.value) {
			changedDuty.value.fixed = true;
		}
	}
);

const resolveTargetDutyId = (payloadDutyId: string | null, dutyID: string): string => {
	return payloadDutyId && payloadDutyId !== dutyID ? payloadDutyId : dutyID;
};

const NON_WORK_TYPES = ['rest', 'compensation', 'reserve'];
const isWorkType = (type?: string | null) => type === 'duty';
const isNonWorkType = (type?: string | null) => !!type && NON_WORK_TYPES.includes(type);

const saveExistingDuty = async (dutyID: string, comment?: string) => {
	const payload = { ...changedDuty.value, changeComment: comment };
	const targetId = resolveTargetDutyId(payload.duty, dutyID);
	return dutyStore.updateDuty(targetId, payload);
};

const saveAsNewDuty = async (comment?: string) => {
	const dutyPayload = changedDuty.value;
	const typeInfo = (dutyPayload.type && DUTY_TYPE_MAP[dutyPayload.type]) || { name: '', nameKey: '' };
	const payload = {
		day: duty.value?.day,
		changeComment: comment,
		fixed: dutyPayload.fixed,
		user: dutyPayload.user,
		type: dutyPayload.type,
		internalNotes: dutyPayload.internalNotes,
		...typeInfo,
	};
	return dutyStore.createNewDuty({ duty: payload });
};

const determineSaveStrategy = (): 'create' | 'update' => {
	const originalType = rawDuty.value?.type;
	const newType = changedDuty.value?.type;

	const originalIsWorkType = isWorkType(originalType) || rawDuty.value?.duty !== null;
	const newIsWorkType = isWorkType(newType);

	if (originalIsWorkType) {
		return newIsWorkType ? 'update' : 'create';
	}

	if (newIsWorkType) {
		return 'update';
	}

	if (isNonWorkType(newType) && newType === originalType) {
		return 'update';
	}

	return 'create';
};

const updateDuty = async (): Promise<void> => {
	if (!hasChanges.value) {
		emit('close', false);
		return;
	}
	if (!duty.value) return;

	loadingState.value = true;
	const dutyID = duty.value.id;
	const comment = changeComment.value || undefined;

	try {
		const strategy = determineSaveStrategy();

		const response = strategy === 'create' ? await saveAsNewDuty(comment) : await saveExistingDuty(dutyID, comment);

		const updatedDutyId = response?.id ?? response?.duty ?? dutyID;
		if (updatedDutyId) {
			const updatedDuty = await init(updatedDutyId);
			emit('duty-updated', updatedDuty);
		}

		if (uiStore.tabClicked) {
			emit('handleTabChange');
		} else if (outerSaveButtonClicked.value) {
			emit('close', false);
		}
	} catch (error) {
		const errorMessage = error?.response?.data?.message || t('data_load_failed');
		alertStore.showAlert(errorMessage, 'error');
		console.error('Failed to update duty:', error);
	} finally {
		loadingState.value = false;
	}
};

const init = async (dutyID: string): Promise<ApiDuty> => {
	try {
		let dutyResponse;
		if (props.customDuty) {
			dutyResponse = JSON.parse(JSON.stringify(props.customDuty));
		} else {
			dutyResponse = await dutyStore.fetchDutyByID(dutyID);
		}
		duty.value = Object.freeze(structuredClone(dutyResponse));
		const targetDay = duty.value?.day;
		const dateString = duty.value?.dayObject?.dateString;

		if (targetDay && dateString) {
			await Promise.all([userStore.loadAvailableDriversByDay(dateString), dutyStore.fetchAvailableDutiesByDay(dateString)]);
		}

		const initialState: ChangedDutyState = {
			duty: duty.value?.type && duty.value.type === 'duty' ? duty.value!.id : null,
			type: duty.value!.type,
			user: duty.value!.user,
			fixed: !!duty.value!.fixed,
			internalNotes: duty.value!.internalNotes || '',
		};

		changedDuty.value = { ...initialState };
		rawDuty.value = { ...initialState };
		return dutyResponse;
	} catch (error) {
		console.error('Failed to initialize duty:', error);
		throw error;
	}
};

const closeCommentDialog = (): void => {
	showCommentDialog.value = false;
	currentActionMode.value = null;
};

const close = () => {
	uiStore.setHasPendingChanges(false);
	emit('close', false);
};

onBeforeMount(async (): Promise<void> => {
	try {
		loadingState.value = true;
		uiStore.tabClicked = false;
		await init(props.dutyID);
		const permissionResult = evaluateEditPermissions(duty.value?.dayObject?.dateString, duty.value);
		edit.value = permissionResult.hasEditPermission;
		partialEdit.value = permissionResult.canPartialEdit;
	} catch (error) {
		console.error('Failed handling active update lifecycle chain:', error);
	} finally {
		loadingState.value = false;
	}
});
</script>

<style scoped>
.style-fixed-card {
	max-height: calc(100vh - 250px);
	display: flex;
	flex-direction: column;
}
.gap-2 {
	gap: 8px;
}
.border-bottom-light {
	border-bottom: 1px solid #f5f5f5;
}
.min-h-0 {
	min-height: 0 !important;
}
.text-warning {
	color: #fb8c00 !important;
}
.text-error {
	color: #ff5252 !important;
}
.save-changes-div {
	background-color: #fff6eb;
	padding: 12px;
}
.main-container {
	/* Set min-height to 0 so flex-grow doesn't force unnecessary stretching */
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	padding-left: 16px;
	padding-right: 16px;
}
.custom-disabled-color:disabled {
	color: #cdcaca !important;
	opacity: 1 !important;
}
.custom-disabled-color:disabled .v-icon {
	color: inherit !important;
	opacity: 1 !important;
}
.overflow-y-auto::-webkit-scrollbar {
	width: 4px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
	background: #e0e0e0;
	border-radius: 10px;
}
.content-card {
	padding: 12px;
	margin-bottom: 16px;
	border: 1px solid rgba(0, 0, 0, 0.12);
	background-color: #fff;
	border-radius: 8px;
	overflow-y: auto;
}
.position-relative {
	position: relative !important;
}
.card-loadingState-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(255, 255, 255, 0.7);
	z-index: 999;
}
.sticky-notes-section {
	position: sticky;
	left: 0;
	right: 0;
	bottom: 30px;
	z-index: 999;
}
.exchange-preference-text {
	color: #000000de;
}
.affected-duty-bar {
	background-color: #fff6eb;
	color: #8a3b00;
	padding: 12px 16px;
}
</style>
